import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { ArrowLeft, ChevronRight, Database, Layers, Search, X, BarChart3, Wallet, Building, Landmark, TrendingDown, CreditCard, Sparkles, Send, MessageSquare, Download, Pencil } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { analyticsOnboardingAPI } from '../../../services/analytics-onboarding-api.service';
import type { AnalyticsAttributesResponse, AnalyticsAttribute, ClientAnalyticsConfig, ConfiguredJourneyInfo } from '../../../types/analytics-onboarding';

/* ═══════════════════════════════════════════════
   Types & Helpers
   ═══════════════════════════════════════════════ */

interface ExploreAttributesViewProps {
  onBack: () => void;
  onManageJourneys?: () => void;
  onError: (err: unknown, context?: Record<string, any>) => void;
  clientConfig?: ClientAnalyticsConfig | null;
  initialJourneyId?: string | null;
}

const filterAttributes = (attrs: AnalyticsAttribute[] | null | undefined, q: string): AnalyticsAttribute[] => {
  if (!attrs) return [];
  if (!q) return attrs;
  const l = q.toLowerCase();
  return attrs.filter(a =>
    a.name.toLowerCase().includes(l) ||
    a.definition?.toLowerCase().includes(l) ||
    a.data_type?.toLowerCase().includes(l) ||
    (a.attributes && filterAttributes(a.attributes, q).length > 0)
  );
};

/**
 * Extract attributes array from a data node.
 * Handles both formats:
 * - Standard: { analytics_attributes: [...] }
 * - Alternative: { attributes: [...], _description: "..." }
 */
const getAttrsFromNode = (node: any): AnalyticsAttribute[] | null => {
  if (!node || typeof node !== 'object') return null;
  // If node IS an array of attributes directly
  if (Array.isArray(node) && node.length > 0 && node[0]?.name) return node;
  if (Array.isArray(node.analytics_attributes)) return node.analytics_attributes;
  if (Array.isArray(node.attributes)) return node.attributes;
  // Key-value attribute format (e.g. fcuIndicators.fraudIndicators):
  // { attrName: { internal_name, definition, data_type, _description } }
  const entries = Object.entries(node).filter(([k]) => k !== '_description' && k !== 'name' && k !== 'type' && k !== 'definition' && k !== 'data_type' && k !== 'internal_name');
  if (entries.length > 0) {
    const allAreAttrs = entries.every(([, v]) =>
      v && typeof v === 'object' && !Array.isArray(v) &&
      (('definition' in (v as any) && 'data_type' in (v as any)) || 'internal_name' in (v as any))
    );
    if (allAreAttrs) {
      return entries.map(([k, v]) => ({
        name: (v as any).internal_name || k,
        definition: (v as any).definition,
        data_type: (v as any).data_type,
        type: 'value',
      } as any));
    }
  }
  return null;
};

/**
 * Detect if a category object uses standard format (with timeframes containing analytics_attributes)
 * or alternative format (with direct attributes arrays and nested sub-sections).
 * Returns 'standard' | 'alternative'
 */
const detectFormat = (data: any): 'standard' | 'alternative' => {
  if (!data || typeof data !== 'object') return 'standard';
  for (const val of Object.values(data)) {
    if (val && typeof val === 'object') {
      if (Array.isArray((val as any).analytics_attributes)) return 'standard';
      if (Array.isArray((val as any).attributes)) return 'alternative';
    }
  }
  // Check if the data itself has attributes
  if (Array.isArray(data.attributes)) return 'alternative';
  return 'standard';
};

/**
 * Count all attributes in a node recursively (for any format).
 */
const countAttrsDeep = (node: any, q: string = ''): number => {
  if (!node || typeof node !== 'object') return 0;
  // If node IS an array of attributes
  if (Array.isArray(node)) {
    if (node.length > 0 && node[0]?.name) return q ? filterAttributes(node, q).length : node.length;
    return 0;
  }
  let count = 0;
  const attrs = getAttrsFromNode(node);
  if (attrs) count += q ? filterAttributes(attrs, q).length : attrs.length;
  for (const [key, val] of Object.entries(node)) {
    if (key === 'attributes' || key === 'analytics_attributes' || key === '_description' || key === 'name' || key === 'type' || key === 'definition' || key === 'data_type') continue;
    if (val && typeof val === 'object') {
      count += countAttrsDeep(val, q);
    }
  }
  return count;
};

const hl = (text: string, q: string): React.ReactNode => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i === -1) return text;
  return <>{text.slice(0, i)}<mark style={{ background: '#fef9c3', borderRadius: 2, padding: '0 2px' }}>{text.slice(i, i + q.length)}</mark>{text.slice(i + q.length)}</>;
};

const humanize = (s: string) => s.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();

// Category color tokens — icon color, bg tint, left accent
const categoryTokens: Record<string, { icon: string; bg: string; accent: string }> = {
  cashflow:           { icon: '#2563eb', bg: '#eff6ff', accent: '#3b82f6' },
  salary:             { icon: '#059669', bg: '#ecfdf5', accent: '#10b981' },
  fcu:                { icon: '#7c3aed', bg: '#f5f3ff', accent: '#8b5cf6' },
  highvaluecredit:    { icon: '#0891b2', bg: '#ecfeff', accent: '#06b6d4' },
  highvaluedebit:     { icon: '#dc2626', bg: '#fef2f2', accent: '#ef4444' },
  overallemianalytics:{ icon: '#ea580c', bg: '#fff7ed', accent: '#f97316' },
  monthlytotal:       { icon: '#2563eb', bg: '#eff6ff', accent: '#3b82f6' },
  monthlyaverage:     { icon: '#2563eb', bg: '#eff6ff', accent: '#3b82f6' },
  bounce:             { icon: '#64748b', bg: '#f8fafc', accent: '#94a3b8' },
  abb:                { icon: '#0284c7', bg: '#f0f9ff', accent: '#0ea5e9' },
  complete:           { icon: '#7c3aed', bg: '#f5f3ff', accent: '#8b5cf6' },
  overall:            { icon: '#059669', bg: '#ecfdf5', accent: '#10b981' },
  monthly:            { icon: '#2563eb', bg: '#eff6ff', accent: '#3b82f6' },
};
const getCategoryToken = (name: string) => {
  const key = name.toLowerCase().replace(/[^a-z]/g, '');
  for (const [k, v] of Object.entries(categoryTokens)) {
    if (key.includes(k)) return v;
  }
  return { icon: '#6366f1', bg: '#eef2ff', accent: '#818cf8' };
};

const categoryIcons: Record<string, React.ReactNode> = {
  cashflow: <BarChart3 style={{ width: 16, height: 16 }} />,
  salary: <Wallet style={{ width: 16, height: 16 }} />,
  fcu: <Building style={{ width: 16, height: 16 }} />,
  highvaluecredit: <Landmark style={{ width: 16, height: 16 }} />,
  highvaluedebit: <TrendingDown style={{ width: 16, height: 16 }} />,
  overallemianalytics: <CreditCard style={{ width: 16, height: 16 }} />,
};
const getCategoryIcon = (name: string) => categoryIcons[name.toLowerCase()] || <BarChart3 style={{ width: 16, height: 16 }} />;

const dataTypeBadgeColors: Record<string, { bg: string; color: string }> = {
  INTEGER: { bg: '#ede9fe', color: '#6d28d9' }, LONG: { bg: '#ede9fe', color: '#6d28d9' },
  STRING: { bg: '#f1f5f9', color: '#475569' }, LIST: { bg: '#dbeafe', color: '#1e40af' },
  MAP: { bg: '#fce7f3', color: '#be185d' }, BOOLEAN: { bg: '#fef3c7', color: '#92400e' },
};
const getTypeBadge = (dt: string) => dataTypeBadgeColors[dt.replace(/\(.*\)/, '').trim()] || { bg: '#dbeafe', color: '#1e40af' };

/* ═══════════════════════════════════════════════
   Reusable primitives
   ═══════════════════════════════════════════════ */

const DataTypeBadge: React.FC<{ type: string }> = ({ type }) => {
  const c = getTypeBadge(type);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
      backgroundColor: c.bg, color: c.color, whiteSpace: 'nowrap',
      letterSpacing: '0.04em', textTransform: 'uppercase', fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace',
    }}>
      {type}
    </span>
  );
};

/* ═══════════════════════════════════════════════
   AI Assistant Drawer
   ═══════════════════════════════════════════════ */

interface AIChatMessage { role: 'user' | 'assistant'; content: string }

/** Lightweight markdown-to-JSX renderer for AI responses */
const renderMarkdown = (text: string): React.ReactNode => {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Bold + inline code
    const inlineFormat = (s: string): React.ReactNode => {
      const parts: React.ReactNode[] = [];
      let remaining = s;
      let key = 0;
      // Process **bold** and `code` inline
      const regex = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
      let match: RegExpExecArray | null;
      let lastIdx = 0;
      while ((match = regex.exec(remaining)) !== null) {
        if (match.index > lastIdx) parts.push(remaining.slice(lastIdx, match.index));
        if (match[2]) parts.push(<strong key={key++}>{match[2]}</strong>);
        else if (match[3]) parts.push(
          <code key={key++} style={{ fontSize: '0.9em', backgroundColor: '#f1f5f9', padding: '1px 5px', borderRadius: 4, color: '#6d28d9' }}>
            {match[3]}
          </code>
        );
        lastIdx = match.index + match[0].length;
      }
      if (lastIdx < remaining.length) parts.push(remaining.slice(lastIdx));
      return parts.length === 1 ? parts[0] : <>{parts}</>;
    };

    if (!line.trim()) {
      elements.push(<div key={i} style={{ height: 8 }} />);
    } else {
      elements.push(<p key={i} style={{ margin: 0 }}>{inlineFormat(line)}</p>);
    }
  }
  return <>{elements}</>;
};

const generateChatId = () => `attr-${crypto.randomUUID?.() || Math.random().toString(36).slice(2)}`;

/** Flatten all attribute names from any response format */
const flattenAttributeNames = (data: AnalyticsAttributesResponse | null): string[] => {
  if (!data) return [];
  const names: string[] = [];
  const collect = (attrs: AnalyticsAttribute[] | null | undefined) => {
    if (!attrs) return;
    for (const a of attrs) {
      names.push(a.name);
      if (a.attributes) collect(a.attributes);
    }
  };
  const walkDeep = (obj: any) => {
    if (!obj || typeof obj !== 'object') return;
    if (Array.isArray(obj)) { collect(obj); return; }
    // Collect attributes/analytics_attributes at this level
    const attrs = getAttrsFromNode(obj);
    if (attrs) collect(attrs);
    // Recurse into sub-objects
    for (const [key, val] of Object.entries(obj)) {
      if (key === 'attributes' || key === 'analytics_attributes') continue;
      if (val && typeof val === 'object') walkDeep(val);
    }
  };
  walkDeep(data);
  return [...new Set(names)].sort();
};

type ChatMode = 'initial' | 'attribute-search' | 'attribute-chat' | 'freeform';

const AIAssistantDrawer: React.FC<{
  open: boolean;
  onClose: () => void;
  clientConfig?: ClientAnalyticsConfig | null;
  allAttributeNames: string[];
}> = ({ open, onClose, allAttributeNames }) => {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatIdRef = useRef<string>(generateChatId());
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [chatMode, setChatMode] = useState<ChatMode>('initial');
  const [attrSearchQuery, setAttrSearchQuery] = useState('');
  const [selectedAttribute, setSelectedAttribute] = useState<string | null>(null);

  // Reset chat and generate new chat_id when drawer reopens
  useEffect(() => {
    if (open) {
      chatIdRef.current = generateChatId();
      setMessages([]);
      setInput('');
      setChatMode('initial');
      setAttrSearchQuery('');
      setSelectedAttribute(null);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleSend = useCallback(async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || isTyping) return;
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setIsTyping(true);

    try {
      const payload = JSON.stringify({ user_query: msg });
      const reply = await analyticsOnboardingAPI.chatWithAI(payload, chatIdRef.current);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setIsTyping(false);
    }
  }, [input, isTyping]);

  const handleAttributeSelect = useCallback(async (attrName: string) => {
    setSelectedAttribute(attrName);
    setChatMode('attribute-chat');
    setMessages([{ role: 'user', content: `Tell me about: ${attrName}` }]);
    setIsTyping(true);

    try {
      const attrInfo = await analyticsOnboardingAPI.getAttributesTyped({ attribute: attrName });
      const payload = JSON.stringify({ user_query: '', attribute_info: attrInfo });
      const reply = await analyticsOnboardingAPI.chatWithAI(payload, chatIdRef.current);
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setIsTyping(false);
    }
  }, []);

  const handleSwitchAttribute = useCallback(() => {
    setChatMode('attribute-search');
    setAttrSearchQuery('');
  }, []);

  const filteredAttrNames = useMemo(() => {
    if (!attrSearchQuery) return allAttributeNames.slice(0, 50);
    const q = attrSearchQuery.toLowerCase();
    return allAttributeNames.filter(n => n.toLowerCase().includes(q)).slice(0, 50);
  }, [allAttributeNames, attrSearchQuery]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.2)',
          zIndex: 9998, backdropFilter: 'blur(2px)',
          animation: 'fadeIn 0.2s ease',
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
        backgroundColor: '#fff', zIndex: 9999,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 30px rgba(0,0,0,0.08)',
        animation: 'slideIn 0.25s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
            }}>
              <Sparkles style={{ width: 16, height: 16, color: '#fff' }} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>AI Attribute Assistant</div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Ask about any analytics metric</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8, border: '1px solid #e2e8f0',
              backgroundColor: '#fff', cursor: 'pointer', color: '#64748b',
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Messages area */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {chatMode === 'initial' && messages.length === 0 ? (
            /* Initial state - two options */
            <div style={{ paddingTop: 20 }}>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
                }}>
                  <MessageSquare style={{ width: 24, height: 24, color: '#7c3aed' }} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
                  How can I help?
                </div>
                <div style={{ fontSize: 13, color: '#94a3b8', maxWidth: 260, margin: '0 auto', lineHeight: 1.5 }}>
                  Choose an option below to get started.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={() => setChatMode('attribute-search')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    border: '1px solid #e2e8f0', borderRadius: 12, backgroundColor: '#fafbfc',
                    cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500, color: '#334155',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#c7d2fe'; e.currentTarget.style.backgroundColor = '#f5f3ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fafbfc'; }}
                >
                  <Search style={{ width: 16, height: 16, color: '#8b5cf6', flexShrink: 0 }} />
                  Want to understand any attribute?
                </button>
                <button
                  onClick={() => { setChatMode('freeform'); setTimeout(() => inputRef.current?.focus(), 100); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    border: '1px solid #e2e8f0', borderRadius: 12, backgroundColor: '#fafbfc',
                    cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500, color: '#334155',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#c7d2fe'; e.currentTarget.style.backgroundColor = '#f5f3ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.backgroundColor = '#fafbfc'; }}
                >
                  <MessageSquare style={{ width: 16, height: 16, color: '#8b5cf6', flexShrink: 0 }} />
                  Something else
                </button>
              </div>
            </div>
          ) : chatMode === 'attribute-search' ? (
            /* Attribute search/picker — redesigned */
            <div style={{ paddingTop: 4 }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 15, color: '#0f172a' }}><b>Find an attribute</b></div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                    {filteredAttrNames.length} attribute{filteredAttrNames.length !== 1 ? 's' : ''} available
                  </div>
                </div>
                <button
                  onClick={() => { setChatMode(messages.length > 0 ? 'attribute-chat' : 'initial'); setAttrSearchQuery(''); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b',
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
                    borderRadius: 6, transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                >
                  <ArrowLeft style={{ width: 12, height: 12 }} />
                  <b>Back</b>
                </button>
              </div>

              {/* Search input */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px',
                border: '1.5px solid #e2e8f0', borderRadius: 12, marginBottom: 16,
                backgroundColor: '#fff', transition: 'all 0.2s',
              }}
                onFocus={e => { (e.currentTarget as HTMLElement).style.borderColor = '#93c5fd'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 0 3px rgba(59,130,246,0.08)'; }}
                onBlur={e => { (e.currentTarget as HTMLElement).style.borderColor = '#e2e8f0'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
              >
                <Search style={{ width: 16, height: 16, color: '#94a3b8', flexShrink: 0 }} />
                <input
                  type="text"
                  placeholder="Type to search attributes..."
                  value={attrSearchQuery}
                  onChange={e => setAttrSearchQuery(e.target.value)}
                  autoFocus
                  style={{
                    flex: 1, border: 'none', outline: 'none', fontSize: 14, color: '#1e293b',
                    backgroundColor: 'transparent', padding: 0,
                  }}
                />
                {attrSearchQuery && (
                  <button onClick={() => setAttrSearchQuery('')} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: '50%', border: 'none', cursor: 'pointer',
                    backgroundColor: '#f1f5f9', color: '#64748b', padding: 0,
                  }}>
                    <X style={{ width: 10, height: 10 }} />
                  </button>
                )}
              </div>

              {/* Results list */}
              <div style={{
                maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2,
                marginRight: -4, paddingRight: 4,
              }}>
                {filteredAttrNames.length > 0 ? filteredAttrNames.map(name => {
                  const q = attrSearchQuery.toLowerCase();
                  const idx = name.toLowerCase().indexOf(q);
                  const highlighted = q && idx >= 0 ? (
                    <>{name.slice(0, idx)}<span style={{ backgroundColor: '#fef9c3', borderRadius: 2, padding: '0 1px' }}>{name.slice(idx, idx + q.length)}</span>{name.slice(idx + q.length)}</>
                  ) : name;

                  return (
                    <button
                      key={name}
                      onClick={() => handleAttributeSelect(name)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', textAlign: 'left', padding: '12px 14px',
                        border: 'none', borderRadius: 10, backgroundColor: 'transparent',
                        cursor: 'pointer', transition: 'all 0.12s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f5f3ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        <div style={{
                          width: 6, height: 6, borderRadius: '50%', backgroundColor: '#c7d2fe', flexShrink: 0,
                        }} />
                        <span style={{ fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {highlighted}
                        </span>
                      </div>
                      <ChevronRight style={{ width: 14, height: 14, color: '#cbd5e1', flexShrink: 0 }} />
                    </button>
                  );
                }) : (
                  <div style={{ padding: '40px 0', textAlign: 'center' }}>
                    <Search style={{ width: 24, height: 24, color: '#e2e8f0', margin: '0 auto 8px' }} />
                    <div style={{ fontSize: 13, color: '#94a3b8' }}>No attributes found</div>
                    {attrSearchQuery && (
                      <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4 }}>
                        Try a different search term
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Chat messages (attribute-chat or freeform) */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Persistent attribute lookup pill */}
              {(chatMode === 'freeform' || (chatMode === 'attribute-chat' && selectedAttribute)) && (
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 14px', borderRadius: 10,
                  backgroundColor: '#f5f3ff', border: '1px solid #e9e5f5', marginBottom: 4,
                }}>
                  {chatMode === 'attribute-chat' && selectedAttribute ? (
                    <>
                      <span style={{ fontSize: 12, color: '#64748b' }}>
                        Discussing: <strong style={{ color: '#0f172a' }}>{selectedAttribute}</strong>
                      </span>
                      <button
                        onClick={handleSwitchAttribute}
                        style={{
                          fontSize: 12, color: '#7c3aed', background: 'none', border: 'none',
                          cursor: 'pointer', padding: '2px 0', fontWeight: 500,
                        }}
                      >
                        Switch Attribute
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setChatMode('attribute-search')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: 13, fontWeight: 500, color: '#7c3aed', padding: 0,
                      }}
                    >
                      <Search style={{ width: 14, height: 14, flexShrink: 0 }} />
                      Want to understand any attribute?
                    </button>
                  )}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '88%', padding: '12px 16px', borderRadius: 14,
                    fontSize: 13, lineHeight: 1.65, color: m.role === 'user' ? '#fff' : '#1e293b',
                    backgroundColor: m.role === 'user' ? '#2563eb' : '#f1f5f9',
                    borderBottomRightRadius: m.role === 'user' ? 4 : 14,
                    borderBottomLeftRadius: m.role === 'assistant' ? 4 : 14,
                  }}>
                    {m.role === 'assistant' ? renderMarkdown(m.content) : m.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{
                    padding: '12px 16px', borderRadius: 14, borderBottomLeftRadius: 4,
                    backgroundColor: '#f1f5f9', display: 'flex', gap: 4, alignItems: 'center',
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#94a3b8', animation: 'pulse 1.2s infinite 0s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#94a3b8', animation: 'pulse 1.2s infinite 0.2s' }} />
                    <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#94a3b8', animation: 'pulse 1.2s infinite 0.4s' }} />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
          )}
        </div>

        {/* Input - visible in attribute-chat and freeform modes */}
        {(chatMode === 'attribute-chat' || chatMode === 'freeform') && (
          <div style={{ padding: '16px 24px 20px', borderTop: '1px solid #f1f5f9' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 14px', borderRadius: 12,
              border: '1px solid #d1d5db', backgroundColor: '#fff',
            }}>
              <input
                ref={inputRef}
                type="text"
                placeholder="Ask about an attribute..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSend()}
                style={{
                  flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#1e293b',
                  backgroundColor: 'transparent', padding: 0,
                }}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim()}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 32, height: 32, borderRadius: 8, border: 'none', cursor: input.trim() ? 'pointer' : 'default',
                  backgroundColor: input.trim() ? '#2563eb' : '#e2e8f0',
                  color: input.trim() ? '#fff' : '#94a3b8',
                  transition: 'all 0.15s',
                }}
              >
                <Send style={{ width: 14, height: 14 }} />
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#cbd5e1', textAlign: 'center', marginTop: 10 }}>
              AI responses are for reference only. Verify critical data independently.
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
        @keyframes pulse { 0%,100% { opacity: 0.4 } 50% { opacity: 1 } }
      `}</style>
    </>
  );
};

/* ═══════════════════════════════════════════════
   AttributeRow
   ═══════════════════════════════════════════════ */

const AttributeRow: React.FC<{ attr: AnalyticsAttribute; q: string; depth?: number; isLast?: boolean }> = ({ attr, q, depth = 0, isLast = false }) => {
  const [open, setOpen] = useState(false);
  const hasKids = !!(attr.attributes && attr.attributes.length > 0);
  const [hovered, setHovered] = useState(false);

  return (
    <>
      <tr
        onClick={hasKids ? () => setOpen(!open) : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          cursor: hasKids ? 'pointer' : 'default',
          borderBottom: isLast && !open ? 'none' : '1px solid #f3f4f6',
          backgroundColor: hovered ? '#fafafa' : depth > 0 ? '#fdfeff' : 'transparent',
          transition: 'background-color 0.12s',
        }}
      >
        <td style={{ padding: '12px 16px', paddingLeft: 16 + depth * 20, fontSize: 12.5, fontWeight: 500, color: '#111827', verticalAlign: 'top', width: '30%' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            {hasKids && (
              <ChevronRight style={{
                width: 13, height: 13, color: '#9ca3af', flexShrink: 0, marginTop: 1,
                transform: open ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.18s ease',
              }} />
            )}
            {!hasKids && depth > 0 && <span style={{ width: 13, flexShrink: 0, display: 'inline-block' }} />}
            <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, SF Mono, Menlo, monospace', fontSize: 12, color: depth > 0 ? '#374151' : '#111827', letterSpacing: '-0.01em' }}>
              {hl(attr.name, q)}
            </span>
            {attr.type === 'custom' && (
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, backgroundColor: '#ede9fe', color: '#6d28d9', marginLeft: 2, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', flexShrink: 0 }}>custom</span>
            )}
          </div>
        </td>
        <td style={{ padding: '12px 16px', fontSize: 13, color: '#4b5563', verticalAlign: 'top', lineHeight: 1.55 }}>
          {attr.definition ? <span style={{ color: '#374151' }}>{hl(attr.definition, q)}</span> : <span style={{ color: '#d1d5db', fontSize: 12 }}>No definition</span>}
        </td>
        <td style={{ padding: '12px 16px', verticalAlign: 'top', width: 110, textAlign: 'right' }}>
          {attr.data_type ? <DataTypeBadge type={attr.data_type} /> : attr.type === 'custom' ? <DataTypeBadge type="CUSTOM" /> : <span style={{ color: '#d1d5db', fontSize: 12 }}>—</span>}
        </td>
      </tr>
      {hasKids && open && attr.attributes!.map((child, i) => (
        <AttributeRow key={child.name} attr={child} q={q} depth={depth + 1} isLast={i === attr.attributes!.length - 1} />
      ))}
    </>
  );
};

/* ═══════════════════════════════════════════════
   TimeframeTabs
   ═══════════════════════════════════════════════ */

const TimeframeTabs: React.FC<{ data: Record<string, any>; query: string }> = ({ data, query }) => {
  const format = detectFormat(data);

  // For alternative format: collect sub-sections that have attributes (direct or nested)
  // For standard format: collect timeframes with analytics_attributes
  const tabs = Object.keys(data).filter(key => {
    if (key === '_description' || key === 'attributes') return false;
    const val = data[key];
    if (!val || typeof val !== 'object') return false;
    if (format === 'standard') return !!getAttrsFromNode(val);
    // Alternative: has attributes directly or has nested children with attributes
    return countAttrsDeep(val) > 0;
  });

  // If no tabs but data itself has attributes, show them directly
  const directAttrs = getAttrsFromNode(data);
  if (tabs.length === 0 && directAttrs && directAttrs.length > 0) {
    const filtered = filterAttributes(directAttrs, query);
    if (filtered.length === 0) return null;
    return (
      <div style={{ padding: '16px 20px 20px' }}>
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '9px 16px', fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.07em', width: '30%', borderBottom: '1px solid #f3f4f6' }}>Attribute</th>
                <th style={{ padding: '9px 16px', fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '1px solid #f3f4f6' }}>Definition</th>
                <th style={{ padding: '9px 16px', fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.07em', width: 110, borderBottom: '1px solid #f3f4f6' }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => <AttributeRow key={a.name} attr={a} q={query} isLast={i === filtered.length - 1} />)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  const [active, setActive] = useState(tabs[0] || '');
  if (tabs.length === 0) return null;

  const activeNode = data[active];
  const activeAttrs = getAttrsFromNode(activeNode);
  const filtered = activeAttrs ? filterAttributes(activeAttrs, query) : [];

  return (
    <div style={{ padding: '14px 16px 18px' }}>
      {/* Tab pills — using div to avoid Equal DS button interference */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12, padding: '3px 3px', backgroundColor: '#f8fafc', borderRadius: 8, width: 'fit-content', border: '1px solid #f1f5f9' }}>
        {tabs.map(tf => {
          const isActive = tf === active;
          const tfAttrs = getAttrsFromNode(data[tf]);
          const count = tfAttrs ? filterAttributes(tfAttrs, query).length : countAttrsDeep(data[tf], query);
          if (query && count === 0) return null;
          return (
            <div
              key={tf}
              role="button" tabIndex={0}
              onClick={() => setActive(tf)}
              onKeyDown={e => e.key === 'Enter' && setActive(tf)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                fontSize: 12, fontWeight: isActive ? 600 : 400,
                textTransform: 'capitalize',
                backgroundColor: isActive ? '#fff' : 'transparent',
                color: isActive ? '#111827' : '#64748b',
                border: isActive ? '1px solid #e2e8f0' : '1px solid transparent',
                boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.07)' : 'none',
                transition: 'all 0.1s',
                userSelect: 'none',
              }}
            >
              <span>{tf}</span>
              <span style={{
                fontSize: 10, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                color: isActive ? '#2563eb' : '#9ca3af',
                backgroundColor: isActive ? '#eff6ff' : '#f1f5f9',
                padding: '0px 4px', borderRadius: 3, lineHeight: '16px',
              }}>{count}</span>
            </div>
          );
        })}
      </div>

      {filtered.length > 0 ? (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={{ padding: '9px 16px', fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.07em', width: '30%', borderBottom: '1px solid #f3f4f6' }}>Attribute</th>
                <th style={{ padding: '9px 16px', fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.07em', borderBottom: '1px solid #f3f4f6' }}>Definition</th>
                <th style={{ padding: '9px 16px', fontSize: 10.5, fontWeight: 600, color: '#9ca3af', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '0.07em', width: 110, borderBottom: '1px solid #f3f4f6' }}>Type</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a, i) => <AttributeRow key={a.name} attr={a} q={query} isLast={i === filtered.length - 1} />)}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No attributes match your search.</div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   CategoryCard
   ═══════════════════════════════════════════════ */

const CategoryCard: React.FC<{ name: string; data: any; query: string }> = ({ name, data, query }) => {
  const [manualOpen, setManualOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  if (!data || typeof data !== 'object') return null;

  const total = countAttrsDeep(data, query);
  const hasResults = query ? total > 0 : true;
  if (query && !hasResults) return null;

  const open = query ? hasResults : manualOpen;
  const totalUnfiltered = countAttrsDeep(data);
  const subSectionCount = Object.keys(data).filter(k => k !== '_description' && k !== 'attributes' && data[k] && typeof data[k] === 'object').length;
  const token = getCategoryToken(name);

  return (
    <div style={{
      marginBottom: 6,
      borderRadius: 10,
      border: `1px solid ${open ? token.accent + '50' : '#e9edf2'}`,
      overflow: 'hidden',
      backgroundColor: '#fff',
      boxShadow: open ? `0 2px 8px ${token.accent}12` : '0 1px 2px rgba(0,0,0,0.03)',
      transition: 'border-color 0.15s, box-shadow 0.15s',
    }}>
      {/* Row header — no absolute children, clean border-left */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setManualOpen(!manualOpen)}
        onKeyDown={e => e.key === 'Enter' && setManualOpen(!manualOpen)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 14px',
          borderLeft: `3px solid ${open ? token.accent : 'transparent'}`,
          backgroundColor: open ? `${token.bg}50` : hovered ? '#fafafa' : '#fff',
          cursor: 'pointer',
          transition: 'background-color 0.12s, border-left-color 0.15s',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: open || hovered ? token.bg : '#f8fafc',
            border: `1px solid ${open ? token.accent + '30' : '#e9edf2'}`,
            transition: 'background-color 0.12s, border-color 0.12s',
          }}>
            {React.cloneElement(getCategoryIcon(name) as React.ReactElement, {
              style: { width: 14, height: 14, color: open || hovered ? token.icon : '#94a3b8' }
            })}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', lineHeight: 1.35, letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 560 }}>
              {humanize(name)}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
              <span style={{ fontFamily: 'ui-monospace, monospace' }}>{totalUnfiltered.toLocaleString()}</span>
              {' attributes'}
              {subSectionCount > 0 && <> · {subSectionCount} group{subSectionCount !== 1 ? 's' : ''}</>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span style={{
            fontSize: 12, fontWeight: 600, fontFamily: 'ui-monospace, monospace',
            color: open ? token.icon : '#94a3b8', minWidth: 28, textAlign: 'right',
            transition: 'color 0.15s',
          }}>
            {totalUnfiltered.toLocaleString()}
          </span>
          <ChevronRight style={{
            width: 14, height: 14,
            color: open ? token.icon : '#9ca3af',
            transform: open ? 'rotate(90deg)' : 'rotate(0)',
            transition: 'transform 0.2s ease, color 0.15s',
            flexShrink: 0,
          }} />
        </div>
      </div>

      {/* Expanded content — no extra padding-left, clean border-top */}
      {open && (
        <div style={{ borderTop: '1px solid #f1f5f9' }}>
          <TimeframeTabs data={data} query={query} />
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════
   SectionBlock
   ═══════════════════════════════════════════════ */

const SectionBlock: React.FC<{
  title: string; icon: React.ReactNode; accentBg: string;
  analysis: Record<string, any>; query: string;
}> = ({ title, icon, accentBg, analysis, query }) => {
  const totalAttrs = countAttrsDeep(analysis, query);

  return (
    <div style={{ marginBottom: 44 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div style={{
            width: 26, height: 26, borderRadius: 6, backgroundColor: accentBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid rgba(0,0,0,0.05)',
          }}>
            {React.cloneElement(icon as React.ReactElement, { style: { width: 13, height: 13 } })}
          </div>
          <span style={{ fontSize: 14, color: '#0f172a', fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</span>
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
            backgroundColor: accentBg, color: '#475569',
            border: '1px solid rgba(0,0,0,0.06)',
          }}>
            {Object.keys(analysis).length} categories
          </span>
        </div>
        <span style={{
          fontSize: 11.5, color: '#94a3b8', fontWeight: 500,
          fontFamily: 'ui-monospace, monospace', fontVariantNumeric: 'tabular-nums',
        }}>
          {totalAttrs.toLocaleString()} attributes
        </span>
      </div>

      {/* Category cards */}
      <div>
        {Object.entries(analysis).map(([name, data]) => (
          <CategoryCard key={name} name={name} data={data} query={query} />
        ))}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════ */

export const ExploreAttributesView: React.FC<ExploreAttributesViewProps> = ({ onBack, onManageJourneys, onError, clientConfig, initialJourneyId }) => {
  const [attributes, setAttributes] = useState<AnalyticsAttributesResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [, setSearchFocused] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [journeys, setJourneys] = useState<ConfiguredJourneyInfo[]>([]);
  const [activeJourneyIdx, setActiveJourneyIdx] = useState(0);

  const hasConfig = !!clientConfig?.exchange_application_id;

  // Fetch attributes - dual mode
  useEffect(() => {
    if (hasConfig && clientConfig?.exchange_application_id) {
      // Config exists: fetch configured journeys first
      setIsLoading(true);
      analyticsOnboardingAPI.getConfiguredJourneys(clientConfig.exchange_application_id)
        .then((data: any) => {
          const allJourneys: ConfiguredJourneyInfo[] = Array.isArray(data) ? data : data?.journeys || data?.configured_journeys || [];
          const journeyList = allJourneys.filter(j => j.journey_id !== 'bank_statement_ocr');
          setJourneys(journeyList);
          if (journeyList.length > 0) {
            // If initialJourneyId is provided, find its index
            let startIdx = 0;
            if (initialJourneyId) {
              const foundIdx = journeyList.findIndex(j => j.journey_id === initialJourneyId);
              if (foundIdx >= 0) startIdx = foundIdx;
            }
            setActiveJourneyIdx(startIdx);
            const firstJourney = journeyList[startIdx];
            const type = firstJourney.journey_workflow_config?.workflow_configuration?.analytics_configuration?.analytics_configuration_type;
            const exchangeId = clientConfig?.exchange_application_id;
            // Use instance_id + journey_id for journey-specific catalogue (includes custom attrs)
            return analyticsOnboardingAPI.getAttributesTyped(
              exchangeId ? { instance_id: exchangeId, journey_id: firstJourney.journey_id } : { type }
            ).then(setAttributes);
          } else {
            // No journeys configured yet — fall back to full attribute catalogue
            return analyticsOnboardingAPI.getAttributes().then(setAttributes);
          }
        })
        .catch(err => onError(err, { component: 'ExploreAttributesView' }))
        .finally(() => setIsLoading(false));
    } else {
      // No config: fetch all attributes
      analyticsOnboardingAPI.getAttributes()
        .then(setAttributes)
        .catch(err => onError(err, { component: 'ExploreAttributesView' }))
        .finally(() => setIsLoading(false));
    }
  }, [hasConfig, clientConfig?.exchange_application_id]);

  const handleJourneyChange = useCallback((idx: number) => {
    setActiveJourneyIdx(idx);
    setIsLoading(true);
    const journey = journeys[idx];
    const type = journey?.journey_workflow_config?.workflow_configuration?.analytics_configuration?.analytics_configuration_type;
    const exchangeId = clientConfig?.exchange_application_id;
    // Use instance_id + journey_id for journey-specific catalogue
    analyticsOnboardingAPI.getAttributesTyped(
      exchangeId ? { instance_id: exchangeId, journey_id: journey.journey_id } : { type }
    )
      .then(setAttributes)
      .catch(err => onError(err, { component: 'ExploreAttributesView' }))
      .finally(() => setIsLoading(false));
  }, [journeys, clientConfig, onError]);

  const resultCount = useMemo(() => {
    if (!searchQuery || !attributes) return 0;
    let c = 0;
    const countInSection = (a: any) => {
      if (!a) return;
      for (const s of Object.values(a))
        if (s && typeof s === 'object')
          for (const t of Object.values(s))
            if ((t as any)?.analytics_attributes) c += filterAttributes((t as any).analytics_attributes, searchQuery).length;
    };
    for (const topVal of Object.values(attributes)) {
      if (topVal && typeof topVal === 'object') countInSection(topVal);
    }
    return c;
  }, [searchQuery, attributes]);

  const allAttributeNames = useMemo(() => flattenAttributeNames(attributes), [attributes]);


  const handleDownloadPDF = useCallback(() => {
    if (!attributes) return;
    const doc = new jsPDF();
    const rows: string[][] = [];

    const collectAttrs = (attrs: AnalyticsAttribute[] | null, section: string, category: string, timeframe: string) => {
      if (!attrs) return;
      for (const a of attrs) {
        rows.push([section, category, timeframe, a.name, a.definition || '—', a.data_type || '—', a.type || '—']);
        if (a.attributes) collectAttrs(a.attributes, section, category, timeframe);
      }
    };

    const walkSection = (analysisKey: string, analysisData: any) => {
      if (!analysisData || typeof analysisData !== 'object') return;
      const sectionName = analysisKey.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()).trim();
      for (const [cat, catData] of Object.entries(analysisData)) {
        if (!catData || typeof catData !== 'object') continue;
        const catName = cat.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase()).trim();
        for (const [tf, tfData] of Object.entries(catData as any)) {
          const attrs = (tfData as any)?.analytics_attributes || (tfData as any)?.attributes;
          if (attrs) collectAttrs(attrs, sectionName, catName, tf);
        }
      }
    };

    for (const [key, val] of Object.entries(attributes)) {
      if (val && typeof val === 'object') walkSection(key, val);
    }

    doc.setFontSize(16);
    doc.text('Analytics Attributes', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()} | ${rows.length} attributes`, 14, 28);

    autoTable(doc, {
      startY: 35,
      head: [['Section', 'Category', 'Timeframe', 'Attribute', 'Definition', 'Data Type', 'Type']],
      body: rows,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 20 },
        2: { cellWidth: 16 },
        3: { cellWidth: 28 },
        4: { cellWidth: 55 },
        5: { cellWidth: 18 },
        6: { cellWidth: 14 },
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    doc.save('analytics-attributes.pdf');
  }, [attributes]);

  const sectionAccents = ['#eff6ff', '#ecfdf5', '#fdf4ff', '#fff7ed', '#ecfeff', '#fef2f2'];
  const sectionIcons = [
    <Layers style={{ width: 15, height: 15, color: '#2563eb' }} />,
    <Database style={{ width: 15, height: 15, color: '#059669' }} />,
    <BarChart3 style={{ width: 15, height: 15, color: '#7c3aed' }} />,
    <Wallet style={{ width: 15, height: 15, color: '#ea580c' }} />,
    <Building style={{ width: 15, height: 15, color: '#0891b2' }} />,
    <TrendingDown style={{ width: 15, height: 15, color: '#dc2626' }} />,
  ];

  return (
    <div data-xui="explore" style={{ maxWidth: 1100, margin: '0 auto', padding: '0 0 80px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif' }}>

      {/* ── CSS isolation: data-xui scopes our styles away from Equal DS ── */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes shimmer { 0% { background-position: -600px 0 } 100% { background-position: 600px 0 } }

        /* Reset Equal DS interference on our custom button divs */
        [data-xui="explore"] [data-xbtn] {
          all: initial !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          cursor: pointer !important;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important;
          user-select: none !important;
          white-space: nowrap !important;
          text-decoration: none !important;
        }
        [data-xui="explore"] [data-xbtn="primary"] {
          padding: 8px 16px !important;
          border-radius: 8px !important;
          border: none !important;
          background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%) !important;
          color: #ffffff !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          box-shadow: 0 1px 3px rgba(99,102,241,0.35), 0 0 0 0px rgba(99,102,241,0) !important;
          transition: opacity 0.15s, box-shadow 0.15s !important;
        }
        [data-xui="explore"] [data-xbtn="primary"]:hover {
          opacity: 0.88 !important;
          box-shadow: 0 2px 8px rgba(99,102,241,0.45) !important;
        }
        [data-xui="explore"] [data-xbtn="secondary"] {
          padding: 7px 14px !important;
          border-radius: 8px !important;
          border: 1px solid #e2e8f0 !important;
          background: #ffffff !important;
          color: #374151 !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.06) !important;
          transition: background 0.12s, border-color 0.12s !important;
        }
        [data-xui="explore"] [data-xbtn="secondary"]:hover {
          background: #f8fafc !important;
          border-color: #cbd5e1 !important;
        }
        [data-xui="explore"] [data-xbtn="ghost"] {
          padding: 4px 0 !important;
          background: none !important;
          border: none !important;
          color: #64748b !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          transition: color 0.12s !important;
        }
        [data-xui="explore"] [data-xbtn="ghost"]:hover { color: #0f172a !important; }
        [data-xui="explore"] [data-xbtn="icon"] {
          padding: 0 !important;
          width: 22px !important;
          height: 22px !important;
          border-radius: 50% !important;
          background: #f1f5f9 !important;
          border: none !important;
          color: #64748b !important;
          justify-content: center !important;
          transition: background 0.12s !important;
        }
        [data-xui="explore"] [data-xbtn="icon"]:hover { background: #e2e8f0 !important; }
        [data-xui="explore"] [data-xbtn="edit"] {
          padding: 6px 12px !important;
          border-radius: 7px !important;
          border: 1px solid #e2e8f0 !important;
          background: #ffffff !important;
          color: #374151 !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.04) !important;
          transition: background 0.12s !important;
        }
        [data-xui="explore"] [data-xbtn="edit"]:hover { background: #f8fafc !important; }

        /* Search bar isolation */
        [data-xsearch] {
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          padding: 0 14px !important;
          height: 42px !important;
          background-color: #ffffff !important;
          border-radius: 10px !important;
          border: 1.5px solid #e2e8f0 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
          transition: border-color 0.15s, box-shadow 0.15s !important;
        }
        [data-xsearch]:focus-within {
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 3px rgba(99,102,241,0.1), 0 1px 3px rgba(0,0,0,0.05) !important;
        }
        [data-xsearch] input {
          all: unset !important;
          flex: 1 !important;
          font-size: 14px !important;
          color: #111827 !important;
          background: transparent !important;
        }
        [data-xsearch] input::placeholder { color: #94a3b8 !important; }

        /* Journey dropdown */
        #explore-journey-select {
          padding: 7px 34px 7px 12px !important;
          font-size: 13px !important;
          color: #111827 !important;
          font-weight: 500 !important;
          border: 1.5px solid #e2e8f0 !important;
          border-radius: 8px !important;
          background-color: #fff !important;
          outline: none !important;
          height: 36px !important;
          cursor: pointer !important;
          -webkit-appearance: none !important;
          appearance: none !important;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") !important;
          background-repeat: no-repeat !important;
          background-position: right 10px center !important;
          background-size: 14px !important;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05) !important;
          transition: border-color 0.15s !important;
          max-width: 220px !important;
        }
        #explore-journey-select:hover { border-color: #cbd5e1 !important; }
        #explore-journey-select:focus { border-color: #6366f1 !important; box-shadow: 0 0 0 3px rgba(99,102,241,0.1) !important; }
      `}</style>

      {/* ═══ Top Bar: Breadcrumb ═══ */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div
          data-xbtn="ghost"
          role="button" tabIndex={0}
          onClick={onBack}
          onKeyDown={e => e.key === 'Enter' && onBack()}
        >
          <ArrowLeft style={{ width: 14, height: 14 }} />
          <span>Analytics</span>
          <span style={{ color: '#cbd5e1', margin: '0 1px', fontSize: 15 }}>/</span>
          <span style={{ color: '#334155', fontWeight: 600 }}>Attributes</span>
        </div>

        {/* Action buttons — always visible, not gated on attributes load */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div data-xbtn="primary" role="button" tabIndex={0} onClick={() => setAiOpen(true)} onKeyDown={e => e.key === 'Enter' && setAiOpen(true)}>
            <Sparkles style={{ width: 13, height: 13 }} />
            <span>Ask AI</span>
          </div>
          <div data-xbtn="secondary" role="button" tabIndex={0} onClick={handleDownloadPDF} onKeyDown={e => e.key === 'Enter' && handleDownloadPDF()}>
            <Download style={{ width: 13, height: 13 }} />
            <span>Export PDF</span>
          </div>
        </div>
      </div>

      {/* ═══ Page Title ═══ */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: '0 0 5px', letterSpacing: '-0.02em', lineHeight: 1.25 }}>
          Attribute Reference
        </h1>
        <p style={{ fontSize: 13.5, color: '#64748b', margin: 0, lineHeight: 1.55 }}>
          Complete data dictionary — definitions, data types, and structure for every analytics metric.
        </p>
      </div>

      {/* ═══ Toolbar: Search + Journey ═══ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32, flexWrap: 'wrap' }}>
        {/* Search */}
        <div data-xsearch style={{ flex: 1, minWidth: 200 }}>
          <Search style={{ width: 15, height: 15, color: '#94a3b8', flexShrink: 0 }} />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search attributes, definitions, types…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {searchQuery ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <span style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap', fontWeight: 500 }}>
                {resultCount.toLocaleString()} found
              </span>
              <div
                data-xbtn="icon"
                role="button" tabIndex={0}
                onClick={() => { setSearchQuery(''); inputRef.current?.focus(); }}
                onKeyDown={e => e.key === 'Enter' && (setSearchQuery(''), inputRef.current?.focus())}
              >
                <X style={{ width: 11, height: 11 }} />
              </div>
            </div>
          ) : (
            <kbd style={{ fontSize: 10, fontWeight: 600, color: '#cbd5e1', backgroundColor: '#f8fafc', border: '1px solid #e9edf1', borderRadius: 4, padding: '2px 6px', flexShrink: 0, fontFamily: 'inherit' }}>/</kbd>
          )}
        </div>

        {/* Journey selector */}
        {hasConfig && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 11.5, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>Journey</span>
            {journeys.length > 0 ? (
              <select id="explore-journey-select" value={activeJourneyIdx} onChange={e => handleJourneyChange(Number(e.target.value))}>
                {journeys.map((j, idx) => (
                  <option key={j.journey_id} value={idx}>{j.journey_id}</option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: 12, color: '#64748b', padding: '5px 10px', border: '1px dashed #cbd5e1', borderRadius: 7, whiteSpace: 'nowrap', backgroundColor: '#f8fafc' }}>
                All attributes
              </span>
            )}
            {onManageJourneys && (
              <div data-xbtn="edit" role="button" tabIndex={0} onClick={onManageJourneys} onKeyDown={e => e.key === 'Enter' && onManageJourneys()}>
                <Pencil style={{ width: 11, height: 11 }} />
                <span>{journeys.length > 0 ? 'Edit Journeys' : 'Configure Journeys'}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info banner — no journeys configured */}
      {hasConfig && journeys.length === 0 && attributes && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 12,
          padding: '12px 16px', marginBottom: 24,
          backgroundColor: '#fffbeb', border: '1px solid #fde68a',
          borderRadius: 10, borderLeft: '3px solid #f59e0b',
        }}>
          <span style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}>💡</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 2 }}>
              Configure an analytics journey to see journey-specific attributes
            </div>
            <div style={{ fontSize: 12.5, color: '#b45309', lineHeight: 1.5 }}>
              You're currently viewing the complete attribute catalogue. Once you configure a journey, you can select it from the <b>Journey</b> dropdown above to explore only the attributes relevant to that journey.
            </div>
          </div>
        </div>
      )}

      {/* ═══ Content ═══ */}
      {isLoading ? (
        <div>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ marginBottom: 40 }}>
              <div style={{ height: 20, width: 160, borderRadius: 6, marginBottom: 16, background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)', backgroundSize: '800px 100%', animation: 'shimmer 1.4s infinite' }} />
              {[1, 2, 3].map(j => (
                <div key={j} style={{ height: 52, borderRadius: 10, marginBottom: 8, background: 'linear-gradient(90deg, #f9fafb 25%, #f3f4f6 50%, #f9fafb 75%)', backgroundSize: '800px 100%', animation: `shimmer 1.4s ${j * 0.1}s infinite`, border: '1px solid #f3f4f6' }} />
              ))}
            </div>
          ))}
        </div>
      ) : attributes ? (
        <div>
          {Object.entries(attributes).map(([sectionKey, sectionData], idx) => {
            if (!sectionData || typeof sectionData !== 'object') return null;
            if (countAttrsDeep(sectionData) === 0) return null;
            const title = humanize(sectionKey);
            return (
              <SectionBlock
                key={sectionKey}
                title={title}
                icon={sectionIcons[idx % sectionIcons.length]}
                accentBg={sectionAccents[idx % sectionAccents.length]}
                analysis={sectionData as Record<string, any>}
                query={searchQuery}
              />
            );
          })}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '100px 0', color: '#9ca3af' }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Database style={{ width: 22, height: 22, color: '#d1d5db' }} />
          </div>
          <p style={{ fontSize: 14, margin: '0 0 6px', color: '#374151', fontWeight: 500 }}>No attributes found</p>
          <p style={{ fontSize: 13, margin: 0, color: '#9ca3af' }}>Configure a journey to explore its attributes</p>
        </div>
      )}

      {/* ═══ AI Drawer ═══ */}
      <AIAssistantDrawer open={aiOpen} onClose={() => setAiOpen(false)} clientConfig={clientConfig} allAttributeNames={allAttributeNames} />
    </div>
  );
};
